import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Dialog, Toast } from 'antd-mobile';
import { t } from '../../../../i18n';
import { showDialogWithDebounce } from '../utils/dialogUtils';
import { AccessCode } from './AccessInfoDisplay';
import { LockOutline, CloseCircleFill } from 'antd-mobile-icons';
import { filterAndSortAccessCodes } from '../utils/accessCodeUtils';
import { validateAccessCodeLocation } from '../utils/accessCodeLocationValidation';
import { getCurrentLocation } from '../../../../utils/locationUtils';
import { submitFeedback, cancelFeedback } from '../../graphql/mutations/accessCodeMutations';
import { getUserInfo } from '../../../../utils/auth';
import { showError } from '../../../../utils/exceptionUtils';
import { ApolloError } from '@apollo/client';
import { getAccessCodesRawWithDedup, clearAccessCodesCache } from '../hooks/useAccessCodes';
import { DeliveryDetailPath } from '../../../../enums/DeliveryNavigatePathEnum';
import { formatDateTime24Hour } from '../../../../utils/dateFormatUtils';
import { getLocalizedAccessCodeTypeName } from '../utils/accessCodeUtils';

interface AccessInfoDetailViewProps {
    orderId: number;
    addressId?: number;
    trackingNumber?: string;
    address?: string;
    addressLat?: number;
    addressLon?: number;
    onBack: () => void; // 返回回调
    skipDistanceLimit?: boolean; // 是否跳过500米距离限制
    onNavigateToAdd?: (params: {
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
        orderId: number;
        skipDistanceLimit?: boolean;
    }) => void; // 导航到新增页面的回调（可选，如果不提供则使用路由跳转）
    onNavigateToUpdate?: (params: {
        code: AccessCode;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
        orderId: number;
        skipDistanceLimit?: boolean;
    }) => void; // 导航到更新页面的回调（可选，如果不提供则使用路由跳转）
    refreshTrigger?: number; // 刷新触发器，当值变化时触发刷新
}

const AccessInfoDetailView: React.FC<AccessInfoDetailViewProps> = ({
    orderId,
    addressId,
    trackingNumber,
    address,
    addressLat,
    addressLon,
    onBack,
    skipDistanceLimit = false,
    onNavigateToAdd,
    onNavigateToUpdate,
    refreshTrigger
}) => {
    const navigate = useNavigate();

    const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [addNewDialogVisible, setAddNewDialogVisible] = useState(false);
    // 记录每个code的反馈状态（用于奇偶次点击切换）
    const [feedbackStates, setFeedbackStates] = useState<Record<number, 'valid' | 'invalid' | null>>({});
    // 位置校验loading状态
    const [isValidatingLocation, setIsValidatingLocation] = useState(false);
    // 反馈操作进行中标记（防止连续点击导致多个弹窗，只禁用按钮，不显示全屏loading）
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    // 更新操作进行中标记（防止快速点击导致多个确认弹窗）
    const [isUpdating, setIsUpdating] = useState(false);

    // 辅助函数：刷新accessCodes列表并更新feedbackStates
    const refreshAccessCodesAndFeedbackStates = async (orderId: number, clearCacheFirst: boolean = false) => {
        // 如果需要，先清除缓存（确保获取最新数据）
        if (clearCacheFirst) {
            clearAccessCodesCache(orderId);
        }
        
        // 使用统一的缓存机制（返回原始数据，包含 currentUserFeedback）
        const codes = await getAccessCodesRawWithDedup(orderId);
        // 转换为AccessCode格式
        const formattedCodes: AccessCode[] = codes.map(code => ({
            id: code.id,
            type: code.codeType,
            code: code.accessCode || '',
            status: code.status,
            createdAt: code.accessCodeCreatedAt ?? undefined,
            updatedAt: code.accessCodeUpdatedAt ?? undefined,
            validFeedbackCount: code.validFeedbackCount,
            invalidFeedbackCount: code.invalidFeedbackCount,
            orderAddressId: code.addressId,
            latitude: code.latitude ?? undefined,
            longitude: code.longitude ?? undefined,
            currentDriverCreatedAt: code.currentDriverCreatedAt ?? undefined,
            currentDriverUpdatedAt: code.currentDriverUpdatedAt ?? undefined
        }));
        setAccessCodes(formattedCodes);

        // 更新反馈状态：使用后端返回的当前用户反馈状态
        // 修复：直接设置新状态，而不是合并，避免组件重新挂载时状态残留
        const updatedFeedbackStates: Record<number, 'valid' | 'invalid' | null> = {};
        codes.forEach(code => {
            if (code.currentUserFeedback === 1) {
                // 1=有效反馈（点赞）
                updatedFeedbackStates[code.id] = 'valid';
            } else if (code.currentUserFeedback === 2) {
                // 2=无效反馈（点踩）
                updatedFeedbackStates[code.id] = 'invalid';
            } else {
                // 0=未反馈
                updatedFeedbackStates[code.id] = null;
            }
        });
        setFeedbackStates(updatedFeedbackStates);
    };

    // 从后端获取门禁码列表
    useEffect(() => {
        const fetchAccessCodes = async () => {
            if (!orderId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                await refreshAccessCodesAndFeedbackStates(orderId);
            } catch (error: any) {
                console.error('Failed to fetch access codes:', error);
                // 如果是网络错误（包括包装后的网络错误），显示网络异常提示
                if (error?.networkError || error?.graphQLErrors?.[0]?.extensions?.code === 'INTERNAL_ERROR') {
                    Toast.show({
                        content: t('errors.system.networkError'),
                        position: 'bottom',
                        duration: 2000
                    });
                } else {
                    showError(error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAccessCodes();
    }, [orderId, refreshTrigger]);

    // 当 orderId 变化时，重置弹框状态（防止切换stop时弹框残留）
    useEffect(() => {
        setAddNewDialogVisible(false);
    }, [orderId]);

    // 处理新增Code按钮点击（先校验地址）
    const addNewLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleAddNewClick = async () => {
        // 延迟显示loading（200ms），如果响应很快则不显示，避免闪烁
        addNewLoadingTimeoutRef.current = setTimeout(() => {
            setIsValidatingLocation(true);
        }, 200);

        try {
            await validateAccessCodeLocation({
                addressLat,
                addressLon,
                skipDistanceLimit,
                onValidationPassed: () => {
                    // 清除延迟显示loading的定时器
                    if (addNewLoadingTimeoutRef.current) {
                        clearTimeout(addNewLoadingTimeoutRef.current);
                        addNewLoadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    setAddNewDialogVisible(true);
                },
                onValidationFailed: () => {
                    // 清除延迟显示loading的定时器
                    if (addNewLoadingTimeoutRef.current) {
                        clearTimeout(addNewLoadingTimeoutRef.current);
                        addNewLoadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                },
                isFormSubmit: false
            });
        } catch (error) {
            // 清除延迟显示loading的定时器
            if (addNewLoadingTimeoutRef.current) {
                clearTimeout(addNewLoadingTimeoutRef.current);
                addNewLoadingTimeoutRef.current = null;
            }
            setIsValidatingLocation(false);
            console.error('位置校验失败:', error);
        }
    };

    // 确认新增Code
    const handleConfirmAddNew = () => {
        setAddNewDialogVisible(false);
        if (onNavigateToAdd) {
            onNavigateToAdd({
                addressId,
                trackingNumber,
                address,
                addressLat,
                addressLon,
                orderId,
                skipDistanceLimit
            });
        } else {
            // 使用路由跳转（非地图模式）
            navigate(
                DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                    ':trackingNumber',
                    trackingNumber || ''
                ),
                {
                    state: {
                        addressId,
                        trackingNumber,
                        address,
                        addressLat,
                        addressLon,
                        orderId,
                        fromCodeList: true, // 标记：从 codeList 页面进入
                        skipDistanceLimit
                    }
                }
            );
        }
    };

    // 显示更新确认弹窗的辅助函数（带防抖保护）
    const showUpdateConfirmDialog = (code: AccessCode) => {
        const handler = showDialogWithDebounce(`update_confirm_${code.id}`, {
            content: (
                <div>
                    <div className="text-base font-semibold mb-3 text-center">
                        {t('pages.delivery.orderDetail.updateAccessCode', { code: `#${code.code}` })}
                    </div>
                    <div className="text-base text-center">
                        {t('pages.delivery.orderDetail.updateAccessCodeConfirm', { code: `#${code.code}` })}
                    </div>
                </div>
            ),
            actions: [
                [
                    {
                        key: 'cancel',
                        text: t('pages.delivery.orderDetail.cancel'),
                        onClick: () => {
                            handler?.close();
                            setIsUpdating(false); // 取消时重置状态
                        },
                        style: { color: '#4b5563' }
                    },
                    {
                        key: 'confirm',
                        text: t('pages.delivery.orderDetail.yes'),
                        onClick: () => {
                            handler?.close();
                            setIsUpdating(false); // 确认后重置状态
                            if (onNavigateToUpdate) {
                                onNavigateToUpdate({
                                    code,
                                    addressId,
                                    trackingNumber,
                                    address,
                                    addressLat,
                                    addressLon,
                                    orderId,
                                    skipDistanceLimit
                                });
                            } else {
                                // 使用路由跳转（非地图模式）
                                navigate(
                                    DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_UPDATE_ACCESS_CODE.replace(
                                        ':trackingNumber',
                                        trackingNumber || ''
                                    ),
                                    {
                                        state: {
                                            code,
                                            addressId,
                                            trackingNumber,
                                            address,
                                            addressLat,
                                            addressLon,
                                            orderId,
                                            fromCodeList: true, // 标记：从 codeList 页面进入
                                            skipDistanceLimit
                                        }
                                    }
                                );
                            }
                        },
                        style: { color: '#60a5fa' }
                    }
                ]
            ]
        });
    };

    // 处理更新Code按钮点击（先刷新数据，再校验24小时频率限制，最后校验地址）
    const updateLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleUpdateClick = async (code: AccessCode) => {
        // 如果正在处理更新，忽略新的点击（防止快速点击导致多个确认弹窗）
        if (isUpdating) {
            return;
        }

        setIsUpdating(true);

        try {
            // 1. 先刷新数据，获取最新的门禁码信息（包括最新的 currentDriverUpdatedAt）
            if (orderId) {
                await refreshAccessCodesAndFeedbackStates(orderId, true);
            }

            // 2. 从刷新后的数据中找到对应的 code
            const latestCode = accessCodes.find(c => c.id === code.id);
            if (!latestCode) {
                Toast.show({
                    icon: 'fail',
                    content: t('pages.delivery.orderDetail.codeNotFound'),
                    position: 'center'
                });
                setIsUpdating(false);
                return;
            }

            // 3. 检查24小时频率限制（使用最新数据）
           /* const { canUpdate } = checkUpdateFrequencyLimit(latestCode);
            if (!canUpdate) {
                Toast.show({
                    content: <div style={{ textAlign: 'center' }}>{t('errors.business.accessCode.updateFrequencyLimit.message')}</div>,
                    icon: <div className="flex items-center gap-2 justify-center">
                        <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                        <div className="text-base">{t('errors.business.accessCode.updateFrequencyLimit.title')}</div>
                    </div>,
                    duration: 2000
                });
                setIsUpdating(false);
                return;
            }*/

            // 4. 延迟显示loading（200ms），如果响应很快则不显示，避免闪烁
            updateLoadingTimeoutRef.current = setTimeout(() => {
                setIsValidatingLocation(true);
            }, 200);

            // 5. 通过频率检查，继续走地址校验逻辑
            await validateAccessCodeLocation({
                addressLat,
                addressLon,
                skipDistanceLimit,
                onValidationPassed: () => {
                    // 清除延迟显示loading的定时器
                    if (updateLoadingTimeoutRef.current) {
                        clearTimeout(updateLoadingTimeoutRef.current);
                        updateLoadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    showUpdateConfirmDialog(latestCode);
                    // 注意：isUpdating在弹窗关闭或确认后才会重置
                },
                onValidationFailed: () => {
                    // 清除延迟显示loading的定时器
                    if (updateLoadingTimeoutRef.current) {
                        clearTimeout(updateLoadingTimeoutRef.current);
                        updateLoadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    setIsUpdating(false);
                },
                isFormSubmit: false
            });
        } catch (error) {
            // 清除延迟显示loading的定时器
            if (updateLoadingTimeoutRef.current) {
                clearTimeout(updateLoadingTimeoutRef.current);
                updateLoadingTimeoutRef.current = null;
            }
            setIsValidatingLocation(false);
            setIsUpdating(false);
            console.error('更新前刷新数据失败:', error);
        }
    };

    // 检查24小时频率限制
    const checkUpdateFrequencyLimit = (code: AccessCode): { canUpdate: boolean } => {
        const { currentDriverCreatedAt, currentDriverUpdatedAt } = code;

        // 如果没有记录，允许更新
        if (!currentDriverUpdatedAt) {
            return { canUpdate: true };
        }

        // 如果是首次更新（created_at == updated_at），允许
        if (currentDriverCreatedAt && currentDriverUpdatedAt && currentDriverCreatedAt === currentDriverUpdatedAt) {
            return { canUpdate: true };
        }

        // 检查24小时限制
        const now = Date.now();
        const lastUpdateTime = currentDriverUpdatedAt * 1000; // 转换为毫秒
        const hoursSinceUpdate = (now - lastUpdateTime) / (1000 * 60 * 60);

        if (hoursSinceUpdate < 24) {
            return { canUpdate: false };
        }

        return { canUpdate: true };
    };

    // 处理反馈按钮点击
    const handleFeedbackClick = async (code: AccessCode, feedbackType: 'valid' | 'invalid') => {
        // 如果正在处理反馈，忽略新的点击（防止连续点击导致多个弹窗）
        if (isSubmittingFeedback) {
            return;
        }

        setIsSubmittingFeedback(true);
        try {
            // 不再进行位置校验，直接获取位置（如果获取不到就传null）
            // 使用缓存，但不请求权限（反馈操作不需要强制获取位置）
            const locationResult = await getCurrentLocation({
                useCache: true,
                requestPermissionIfDenied: false
            });

            // 获取司机账户ID
            const userInfo = getUserInfo();
            const driverAccountId = userInfo?.account?.id ? Number(userInfo.account.id) : null;
            if (!driverAccountId) {
                Toast.show({
                    content: t('pages.delivery.orderDetail.userInfoNotFound'),
                    position: 'bottom'
                });
                setIsSubmittingFeedback(false);
                return;
            }

            // 奇偶次点击逻辑
            const currentState = feedbackStates[code.id];
            const isCurrentlySelected = currentState === feedbackType;
            
            if (isCurrentlySelected) {
                // 偶数次点击：取消反馈
                try {
                    // 调用API取消反馈
                    await cancelFeedback({
                        driverAccountId: driverAccountId,
                        accessCode: {
                            id: code.id,
                            codeType: code.type, // 前端使用 type，后端使用 codeType
                            accessCode: code.code,
                            status: code.status
                        }
                    });
                    
                    Toast.show({
                        content: t('pages.delivery.orderDetail.feedbackCancelled'),
                        position: 'bottom'
                    });
                    
                    // API成功后，刷新accessCodes列表和反馈状态（确保UI与后端数据同步）
                    // 清除缓存后刷新，确保获取最新数据
                    if (orderId) {
                        await refreshAccessCodesAndFeedbackStates(orderId, true);
                    }
                    setIsSubmittingFeedback(false);
                } catch (error) {
                    setIsSubmittingFeedback(false);
                    throw error; // 重新抛出错误，让外层catch处理
                }
            } else {
                // 奇数次点击：提交反馈
                
                // 如果是无效反馈，先显示更新密码弹窗
                if (feedbackType === 'invalid' && currentState !== 'invalid') {
                    try {
                        await submitFeedback({
                            driverAccountId: driverAccountId,
                            isValidFeedback: false, // 无效反馈
                            latitude: locationResult.location?.latitude ?? null,
                            longitude: locationResult.location?.longitude ?? null,
                            accessCode: {
                                id: code.id,
                                codeType: code.type,
                                accessCode: code.code,
                                status: code.status
                            },
                            fromPopup: false // 从详情页反馈，不计入popupShownAt
                        });
                        
                        Toast.show({
                            content: t('pages.delivery.orderDetail.feedbackSubmitted'),
                            position: 'bottom'
                        });
                        
                        // API成功后，刷新accessCodes列表和反馈状态（确保UI与后端数据同步）
                        if (orderId) {
                            await refreshAccessCodesAndFeedbackStates(orderId, true);
                        }
                        
                        // 获取刷新后的最新数据
                        const latestCode = accessCodes.find(c => c.id === code.id);
                        if (!latestCode) {
                            Toast.show({
                                icon: 'fail',
                                content: t('pages.delivery.orderDetail.codeNotFound'),
                                position: 'center'
                            });
                            setIsSubmittingFeedback(false);
                            return;
                        }
                        
                        // 显示更新密码弹窗（使用全局防抖key，确保同时只有一个弹窗）
                        return new Promise<void>((resolve) => {
                            const handler = showDialogWithDebounce('update_password_global', {
                                title: t('pages.delivery.orderDetail.updateAccessCode', { code: latestCode.code }),
                                content: t('pages.delivery.orderDetail.updateAccessCodeConfirm', { code: latestCode.code }),
                                onClose: () => {
                                    // 弹窗关闭时（无论是点击按钮还是点击外部区域），重置状态
                                    setIsSubmittingFeedback(false);
                                    resolve();
                                },
                                actions: [
                                    [
                                        {
                                            key: 'cannotProvide',
                                            text: t('pages.delivery.orderDetail.cannotProvide'),
                                            onClick: () => {
                                                handler?.close();
                                                resolve();
                                            },
                                            style: { color: '#4b5563' }
                                        },
                                        {
                                            key: 'update',
                                            text: t('pages.delivery.orderDetail.upload'),
                                            style: { color: '#60a5fa' },
                                            onClick: async () => {
                                                handler?.close();
                                                
                                                // 点击确认更新时，重新获取最新数据并检查24小时频率限制
                                                try {
                                                    // 1. 刷新数据
                                                    if (orderId) {
                                                        await refreshAccessCodesAndFeedbackStates(orderId, true);
                                                    }
                                                    
                                                    // 2. 获取最新数据
                                                    const currentLatestCode = accessCodes.find(c => c.id === code.id);
                                                    if (!currentLatestCode) {
                                                        Toast.show({
                                                            icon: 'fail',
                                                            content: t('pages.delivery.orderDetail.codeNotFound'),
                                                            position: 'center'
                                                        });
                                                        resolve();
                                                        return;
                                                    }
                                                    
                                                    /*// 3. 检查24小时频率限制
                                                    const { canUpdate } = checkUpdateFrequencyLimit(currentLatestCode);
                                                    if (!canUpdate) {
                                                        Toast.show({
                                                            content: <div style={{ textAlign: 'center' }}>{t('errors.business.accessCode.updateFrequencyLimit.message')}</div>,
                                                            icon: <div className="flex items-center gap-2 justify-center">
                                                                <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
                                                                <div className="text-base">{t('errors.business.accessCode.updateFrequencyLimit.title')}</div>
                                                            </div>,
                                                            duration: 2000
                                                        });
                                                        resolve();
                                                        return;
                                                    }*/
                                                    
                                                    // 4. 通过频率检查，进行位置校验
                                                    setIsValidatingLocation(true);
                                                    await validateAccessCodeLocation({
                                                        addressLat,
                                                        addressLon,
                                                        skipDistanceLimit,
                                                        onValidationPassed: () => {
                                                            setIsValidatingLocation(false);
                                                            // 校验通过，跳转到更新页面
                                                            if (onNavigateToUpdate) {
                                                                onNavigateToUpdate({
                                                                    code: currentLatestCode,
                                                                    addressId,
                                                                    trackingNumber,
                                                                    address,
                                                                    addressLat,
                                                                    addressLon,
                                                                    orderId
                                                                });
                                                            } else {
                                                                navigate(
                                                                    DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_UPDATE_ACCESS_CODE.replace(
                                                                        ':trackingNumber',
                                                                        trackingNumber || ''
                                                                    ),
                                                                    {
                                                                        state: {
                                                                            code: currentLatestCode,
                                                                            addressId,
                                                                            trackingNumber,
                                                                            address,
                                                                            addressLat,
                                                                            addressLon,
                                                                            orderId,
                                                                            fromCodeList: true // 标记：从 codeList 页面进入
                                                                        }
                                                                    }
                                                                );
                                                            }
                                                            resolve();
                                                        },
                                                        onValidationFailed: () => {
                                                            setIsValidatingLocation(false);
                                                            resolve();
                                                        },
                                                        isFormSubmit: false
                                                    });
                                                } catch (error) {
                                                    setIsValidatingLocation(false);
                                                    console.error('更新前校验失败:', error);
                                                    resolve();
                                                }
                                            }
                                        }
                                    ]
                                ]
                            });
                        });
                    } catch (error) {
                        console.error('反馈操作失败:', error);
                        
                        // 处理前端网络错误（来电打断时，networkError存在但graphQLErrors为空）
                        if (error instanceof ApolloError) {
                            // 检查是否是网络错误（前端网络中断，请求未到达后端）
                            if (error.networkError) {
                                Toast.show({
                                    content: t('errors.system.networkError'),
                                    position: 'bottom'
                                });
                                setIsSubmittingFeedback(false);
                                return;
                            }
                            
                            // 检查是否是内部错误（请求到达后端但返回内部错误）
                            const graphQLError = error.graphQLErrors?.[0];
                            const errorCode = graphQLError?.extensions?.code;
                            const messageKey = graphQLError?.extensions?.messageKey;
                            
                            // 如果是内部错误，显示网络错误消息
                            if (errorCode === 'INTERNAL_ERROR' || messageKey === 'errors.system.internal') {
                                Toast.show({
                                    content: t('errors.system.networkError'),
                                    position: 'bottom'
                                });
                                setIsSubmittingFeedback(false);
                                return;
                            }
                        }
                        
                        // 其他错误使用默认处理
                        showError(error);
                        setIsSubmittingFeedback(false);
                        return;
                    }
                }
                
                // 非无效反馈或已经是无效反馈状态，直接提交反馈
                try {
                    // 当获取不到经纬度时，使用order地址的经纬度
                    const feedbackLatitude = locationResult.location?.latitude ?? addressLat ?? null;
                    const feedbackLongitude = locationResult.location?.longitude ?? addressLon ?? null;
                    
                    await submitFeedback({
                        driverAccountId: driverAccountId,
                        isValidFeedback: feedbackType === 'valid',
                        latitude: feedbackLatitude,
                        longitude: feedbackLongitude,
                        accessCode: {
                            id: code.id,
                            codeType: code.type, // 前端使用 type，后端使用 codeType
                            accessCode: code.code,
                            status: code.status
                        },
                        fromPopup: false // 从详情页反馈，不计入popupShownAt
                    });
                    
                    Toast.show({
                        content: t('pages.delivery.orderDetail.feedbackSubmitted'),
                        position: 'bottom'
                    });

                    // API成功后，刷新accessCodes列表和反馈状态（确保UI与后端数据同步）
                    // 清除缓存后刷新，确保获取最新数据
                    if (orderId) {
                        await refreshAccessCodesAndFeedbackStates(orderId, true);
                    }
                    setIsSubmittingFeedback(false);
                } catch (error) {
                    setIsSubmittingFeedback(false);
                    throw error; // 重新抛出错误，让外层catch处理
                }
            }
        } catch (error) {
            console.error('反馈操作失败:', error);
            
            // 处理前端网络错误（来电打断时，networkError存在但graphQLErrors为空）
            if (error instanceof ApolloError) {
                // 检查是否是网络错误（前端网络中断，请求未到达后端）
                if (error.networkError) {
                    Toast.show({
                        content: t('errors.system.networkError'),
                        position: 'bottom'
                    });
                    setIsSubmittingFeedback(false);
                    return;
                }
                
                // 检查是否是内部错误（请求到达后端但返回内部错误）
                const graphQLError = error.graphQLErrors?.[0];
                const errorCode = graphQLError?.extensions?.code;
                const messageKey = graphQLError?.extensions?.messageKey;
                
                // 如果是内部错误，显示网络错误消息
                if (errorCode === 'INTERNAL_ERROR' || messageKey === 'errors.system.internal') {
                    Toast.show({
                        content: t('errors.system.networkError'),
                        position: 'bottom'
                    });
                    setIsSubmittingFeedback(false);
                    return;
                }
            }
            
            // 其他错误使用默认处理
            showError(error);
            setIsSubmittingFeedback(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-full bg-gray-50">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">{t('pages.delivery.orderDetail.loading')}</div>
                </div>
            </div>
        );
    }

    if (!accessCodes || accessCodes.length === 0) {
        return (
            <div className="flex flex-col h-full bg-gray-50">
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">
                        {t('pages.delivery.orderDetail.noAccessInfo')}
                    </div>
                </div>
            </div>
        );
    }

    // 按类型分组、筛选和排序
    const sortedCodes = filterAndSortAccessCodes(
        accessCodes || [],
        addressLat,
        addressLon
    );

    return (
        <div className="flex flex-col h-full bg-gray-50 relative">
            {/* 位置校验Loading遮罩 */}
            {isValidatingLocation && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"/>
                            <div className="text-gray-700 text-base">{t('pages.delivery.orderDetail.loading')}</div>
                        </div>
                    </div>
                </div>
            )}
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
                <Card className="mb-4">
                    {/* Address Module */}
                    <div className="mb-4 pb-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-base font-semibold text-gray-800">
                                {t('pages.delivery.orderDetail.address')}
                            </div>
                            <button
                                onClick={handleAddNewClick}
                                className="text-blue-400 text-sm flex items-center gap-0.5 px-1.5 py-0.5 border border-blue-400 rounded hover:text-blue-600 hover:border-blue-600"
                            >
                                <span>{t('pages.delivery.orderDetail.addNew')}</span>
                                <LockOutline className="text-xs" />
                            </button>
                        </div>
                        <div className="text-base text-gray-700 break-words">
                            {address || '-'}
                        </div>
                    </div>

                    {/* Access Codes List */}
                    <div className="space-y-4">
                        {sortedCodes.map((code, index) => {
                            // 类型转换：将 AccessCodeForSort 转换为 AccessCode
                            const accessCode: AccessCode = {
                                id: code.id,
                                type: code.type,
                                code: code.code,
                                status: code.status,
                                validFeedbackCount: code.validFeedbackCount,
                                invalidFeedbackCount: code.invalidFeedbackCount,
                                updatedAt: code.updatedAt ?? undefined,
                                createdAt: code.createdAt ?? undefined,
                                latitude: code.latitude ?? undefined,
                                longitude: code.longitude ?? undefined
                            };
                            return (
                                <div
                                    key={accessCode.id || index}
                                    className="p-4 bg-white border border-gray-200 rounded-lg"
                                >
                                    {/* Code Header */}
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-lg text-gray-400 font-semibold">
                                            {accessCode.type ? getLocalizedAccessCodeTypeName(accessCode.type, t) : accessCode.type}
                                        </div>
                                        <button
                                            onClick={() => handleUpdateClick(accessCode)}
                                            disabled={isUpdating}
                                            className={`text-blue-400 text-sm flex items-center gap-0.5 px-1.5 py-0.5 border border-blue-400 rounded hover:text-blue-600 hover:border-blue-600 ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <span>{t('pages.delivery.orderDetail.update')}</span>
                                            <LockOutline className="text-xs" />
                                        </button>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200 mb-3"></div>

                                    {/* Code Value */}
                                    <div className="text-2xl font-semibold text-gray-600 mb-2">
                                        {accessCode.code}
                                    </div>

                                    {/* Creation Time - 显示业务意义上的创建时间（access_code_updated_at的MIN值） */}
                                    {accessCode.createdAt && (
                                        <div className="text-xs text-gray-500 mb-3">
                                            {t('pages.delivery.orderDetail.created')}: {formatDateTime24Hour(new Date(accessCode.createdAt * 1000))}
                                        </div>
                                    )}

                                    {/* Feedback Section */}
                                    <div className="flex items-center pt-3 border-t border-gray-100">
                                        <button
                                            onClick={() => handleFeedbackClick(accessCode, 'valid')}
                                            disabled={isSubmittingFeedback}
                                            className={`flex-1 flex items-center justify-center gap-2 transition-colors ${
                                                feedbackStates[accessCode.id] === 'valid'
                                                    ? 'text-blue-600'
                                                    : 'text-blue-400 hover:text-blue-600'
                                            } ${isSubmittingFeedback ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill={feedbackStates[accessCode.id] === 'valid' ? 'currentColor' : 'none'}
                                                stroke="currentColor"
                                                strokeWidth={feedbackStates[accessCode.id] === 'valid' ? '0' : '2'}
                                            >
                                                <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
                                            </svg>
                                            <span className="text-sm font-medium">
                                                {accessCode.validFeedbackCount || 0}
                                            </span>
                                        </button>
                                        <div className="w-px h-6 bg-gray-200"></div>
                                        <button
                                            onClick={() => handleFeedbackClick(accessCode, 'invalid')}
                                            disabled={isSubmittingFeedback}
                                            className={`flex-1 flex items-center justify-center gap-2 transition-colors ${
                                                feedbackStates[accessCode.id] === 'invalid'
                                                    ? 'text-red-600'
                                                    : 'text-red-400 hover:text-red-600'
                                            } ${isSubmittingFeedback ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {/* 点踩图标 */}
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill={feedbackStates[accessCode.id] === 'invalid' ? 'currentColor' : 'none'}
                                                stroke="currentColor"
                                                strokeWidth={feedbackStates[accessCode.id] === 'invalid' ? '0' : '2'}
                                            >
                                                <g transform="scale(-1, 1) translate(-24, 0)">
                                                    <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
                                                </g>
                                            </svg>
                                            <span className="text-sm font-medium">
                                                {accessCode.invalidFeedbackCount || 0}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            {/* Add New Dialog */}
            <Dialog
                visible={addNewDialogVisible}
                content={
                    <div>
                        <div className="text-base font-semibold mb-3 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoTitle')}
                        </div>
                        <div className="text-base text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoConfirm')}
                        </div>
                    </div>
                }
                actions={[
                    [
                        {
                            key: 'cancel',
                            text: t('pages.delivery.orderDetail.cancel'),
                            onClick: () => setAddNewDialogVisible(false),
                            style: { color: '#4b5563' }
                        },
                        {
                            key: 'confirm',
                            text: t('pages.delivery.orderDetail.yes'),
                            onClick: handleConfirmAddNew,
                            style: { color: '#60a5fa' }
                        }
                    ]
                ]}
                onClose={() => setAddNewDialogVisible(false)}
            />
        </div>
    );
};

export default AccessInfoDetailView;

